/**
 * Numeric column statistics — the single source of truth for descriptive
 * stats over a bag of numeric values, consumed by the dataset auto-stats
 * profiler (plugin-chat).
 */
import { mean } from 'simple-statistics';

/**
 * Stats for numeric columns.
 *
 * When a numeric-classified column contains no real numbers (e.g. every
 * value was null, undefined, or NaN), the summary statistics are `null`
 * rather than fabricated zeros — reporting min/max/mean = 0 on an empty
 * column silently misleads the LLM into treating "no data" as "all
 * measurements were zero". Consumers must handle the null variant when
 * rendering.
 */
export interface NumericColumnStats {
  type: 'numeric';
  count: number;
  nullCount: number;
  min: number | null;
  max: number | null;
  mean: number | null;
}

/**
 * Compute statistics for a numeric column.
 */
export function computeNumericStats(values: unknown[]): NumericColumnStats {
  const numbers = values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
  const nullCount = values.filter((v) => v === null || v === undefined).length;

  if (numbers.length === 0) {
    // No real numbers to aggregate — return `null` for every summary
    // statistic. Fabricating zeros here used to silently nudge the LLM
    // into treating an empty column as "all measurements were zero",
    // which is the autoStats half of the null-values-produce-sum-of-0
    // production bug.
    return {
      type: 'numeric',
      count: 0,
      nullCount,
      min: null,
      max: null,
      mean: null,
    };
  }

  return {
    type: 'numeric',
    count: numbers.length,
    nullCount,
    min: Math.min(...numbers),
    max: Math.max(...numbers),
    mean: mean(numbers),
  };
}
